import Humanograma from 'components/Humanograma';
import { PersonAvatar, PersonFaceSource, initials } from 'components/person-avatar';
import { formatDayInSaoPaulo } from 'pages/MissaoDestaque/deleted-tasks';

import { rostoDoAutor } from '../identidade-do-participante';
import { PilulaDaEtapa } from './AgendaDaRealizacao';
import { FASE_NAO_IDENTIFICADA } from '../realizacoes-do-semestre';

/** Quem cadastrou, com os dados da dica do `Humanograma`. */
export interface AutorDoCartao {
  chave: string;
  nomeGuerra: string;
  nome?: string;
  funcao?: string;
  prefixo?: string;
  dependencia?: string;
  rosto: PersonFaceSource;
}

/** Autor como `find/entregas` o traz: os mesmos campos que o `TaskItem` passa ao `Humanograma` do cadastrante. */
export function autorDaRealizacao(data: any): AutorDoCartao {
  return {
    chave: data.owner,
    nomeGuerra: data.owner_name_guerra ?? '',
    nome: data.owner_name,
    funcao: data.owner_funcao,
    prefixo: data.prefixo,
    dependencia: data.owner_nome_dependencia,
    rosto: rostoDoAutor(data),
  };
}

/** Dica do `Humanograma` para o autor, nos campos que o componente espera. */
export function humanogramaDoAutor(autor: AutorDoCartao) {
  return {
    _id: autor.chave,
    nome: autor.nome || autor.nomeGuerra,
    funcao: autor.funcao,
    prefixo: autor.prefixo,
    nome_dependencia: autor.dependencia,
  };
}

export interface CabecalhoDaRealizacaoProps {
  /** Id do título: o cartão o usa em `aria-labelledby`. */
  tituloId: string;
  titulo: string;
  autor: AutorDoCartao;
  /** Realização do próprio usuário: "Cadastrada por você · …". */
  propria: boolean;
  /** Título da fase mensal de cadastramento. */
  rotuloDaFase: string;
  cadastradaEm: number;
  /** Texto da pílula (RDV-14); vazio, nenhuma pílula. */
  pilula: string;
  /** "Pontos recebidos" de hoje (`points` do `TaskItem`); o cartão de excluída não mostra. */
  pontos?: number;
}

/** Cabeçalho do cartão (RDV-16): autor no `Humanograma` (RDV-25), fase, data do cadastro, pílula e pontos. */
export default function CabecalhoDaRealizacao({
  tituloId,
  titulo,
  autor,
  propria,
  rotuloDaFase,
  cadastradaEm,
  pilula,
  pontos,
}: CabecalhoDaRealizacaoProps) {
  const fase = rotuloDaFase === FASE_NAO_IDENTIFICADA ? rotuloDaFase : `Fase ${rotuloDaFase}`;

  return (
    <header className="flex flex-wrap items-start gap-3.5">
      <PersonAvatar
        {...autor.rosto}
        initials={initials(autor.nomeGuerra)}
        size={42}
        initialsClassName={`${propria ? 'bg-brand-yellow' : 'bg-brand-green'} font-display text-[13px] font-bold text-brand-navy`}
      />
      <div className="min-w-0 flex-1">
        {/* `leading-[1.3]`: o rsuite.min.css global põe `h3 { line-height: 42px }` e o título desceria. */}
        <h3 id={tituloId} className="m-0 font-display text-base font-bold leading-[1.3] text-brand-blue-dark">
          {titulo || '---'}
        </h3>
        <p className="m-0 mt-[5px] text-[11.5px] leading-[1.5] text-content-soft">
          {propria ? 'Cadastrada por você · ' : 'Cadastrada por '}
          <Humanograma data={humanogramaDoAutor(autor)}>
            <span className="font-bold text-link hover:text-link-hover">{`${autor.nomeGuerra} (${autor.chave})`}</span>
          </Humanograma>
          {` · ${fase} · cadastrada em ${formatDayInSaoPaulo(cadastradaEm)}`}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-[7px]">
        <PilulaDaEtapa status={pilula} variante="cartao" />
        {typeof pontos === 'number' && (
          <dl className="m-0 flex flex-row-reverse items-baseline gap-1">
            <dt className="text-[10px] text-content-soft">Pontos recebidos</dt>
            <dd className="m-0 font-display text-lg font-bold leading-none text-brand-blue">{pontos}</dd>
          </dl>
        )}
      </div>
    </header>
  );
}
